/**
 * Account-based giving totals for the paid claim guide.
 *
 * The guide switches from the checker's typed-in figure to what is actually in
 * the donor's account for the claim year. Only Gift Aided donations qualify
 * for higher-rate relief, and the figure quoted to HMRC is the amount GIVEN
 * (net), never the Gift Aided total.
 */

import type { DonorDonation } from '@/types/donorFinancialYear'
import { donationInTaxYearPeriod } from '@/helpers/taxPackDonations'

/** Gift Aid was applied (Swiftaid rows) or declared by the donor (manual rows). */
export function isGiftAidedDonation(donation: DonorDonation): boolean {
  return donation.giftaid > 0 || donation.giftAidDeclared === true
}

/** Gift Aided donations within a UK tax year period, e.g. "2025-2026". */
export function giftAidedDonationsForPeriod(
  donations: DonorDonation[],
  periodKey: string
): DonorDonation[] {
  return donations.filter(
    (donation) =>
      isGiftAidedDonation(donation) &&
      donationInTaxYearPeriod(donation, periodKey)
  )
}

/** Total amount GIVEN (net, pence). This is the figure a donor quotes to HMRC. */
export function totalGivenPence(donations: DonorDonation[]): number {
  return donations.reduce((sum, donation) => sum + donation.amount, 0)
}
